import React, { useState } from 'react';
import { Button, useToast } from '@crisellevaldez/speculo-ui';
import { Sparkles, Loader2 } from 'lucide-react';
import { generateVoicemail } from '../../../services/voicemailService';

interface RegenerateVoicemailButtonProps {
  /** Flow ID for generating voicemail */
  flowId: string;
  /** Flow description used to generate voicemail */
  flowDescription: string;
  /** Callback when voicemail is regenerated successfully - receives the new content when available */
  onRegenerated: (voicemail?: string) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Reusable AI voicemail regeneration button component
 * Uses the voicemail generation service to create new voicemail scripts
 */
export const RegenerateVoicemailButton: React.FC<RegenerateVoicemailButtonProps> = ({
  flowId,
  flowDescription,
  onRegenerated,
  disabled = false,
  className = '',
}) => {
  const { showToast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleRegenerate = async () => {
    if (!flowId || !flowDescription) {
      showToast({
        message: 'Flow ID or description is missing',
        variant: 'error',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const result = await generateVoicemail(flowId, flowDescription);

      if (result.voicemail) {
        onRegenerated(result.voicemail);
        showToast({
          message: 'Voicemail generated.',
          variant: 'success',
        });
        return;
      }

      if (result.queued) {
        showToast({
          message: 'Voicemail generation started. The existing voicemail will stay in place until the new one is ready.',
          variant: 'success',
        });
        return;
      }

      showToast({
        message: 'Voicemail request sent, but no updated voicemail content was returned yet.',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to generate voicemail',
        variant: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      size="sm"
      className={`gap-2 h-8 btn-ai-gradient px-2 ${className}`}
      onClick={handleRegenerate}
      disabled={disabled || isGenerating}
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Generating Voicemail...
        </>
      ) : (
        <>
          <Sparkles className="w-4 h-4" />
          Generate Voicemail
        </>
      )}
    </Button>
  );
};

export default RegenerateVoicemailButton;
